import { green, red, purple, cyan, orange } from '@material-ui/core/colors';

export const PUBLIC_PATHS = ['/login', '/forgot-password', '/reset-password', '/register'];

export const LOAN_STATUSES = ['ACTIVE', 'VOIDED', 'CLOSED', 'RETURNED', 'NOT_FUNDING'];

// Permission presets: selecting one fills in its roles. Presets are additive —
// checking adds its roles, unchecking removes them except any still required by
// another preset that remains fully selected.
export const ROLE_PRESETS = [
    { name: 'CS Admin', roles: ['cs', 'daily_processing_read', 'dealer_browser'] },
    { name: 'Super Admin', roles: ['admin', 'cs', 'daily_processing_read', 'dealer_browser'] },
    { name: 'Admin DP + Write', roles: ['admin', 'daily_processing_write'] },
    { name: 'Admin DP + Write + ACH', roles: ['ach_admin', 'admin', 'daily_processing_write'] },
];

export const isPresetSelected = (preset, selectedRoles) =>
    preset.roles.every((r) => selectedRoles.includes(r));

// Roles that would remain selected if `preset` were deselected, because another
// fully-selected preset still requires them.
const rolesKeptByOtherPresets = (preset, selectedRoles) =>
    ROLE_PRESETS
        .filter((p) => p.name !== preset.name && isPresetSelected(p, selectedRoles))
        .reduce((acc, p) => acc.concat(p.roles), []);

// Next role selection after toggling `preset`. Additive: selecting unions in its
// roles; deselecting removes them except any still required by another preset.
export const togglePreset = (preset, selectedRoles) => {
    if (!isPresetSelected(preset, selectedRoles)) {
        return [...new Set([...selectedRoles, ...preset.roles])];
    }
    const kept = rolesKeptByOtherPresets(preset, selectedRoles);
    return selectedRoles.filter((r) => !preset.roles.includes(r) || kept.includes(r));
};

// A preset is "locked on" when all of its roles are already required by another
// fully-selected preset, so deselecting it would do nothing. We show these as
// checked-but-disabled with a tooltip rather than a checkbox that ignores clicks.
export const isPresetLocked = (preset, selectedRoles) => {
    if (!isPresetSelected(preset, selectedRoles)) return false;
    const kept = rolesKeptByOtherPresets(preset, selectedRoles);
    return preset.roles.every((r) => kept.includes(r));
};

// The other selected preset that fully contains this one, for tooltip text.
export const lockingPresetName = (preset, selectedRoles) => {
    const covering = ROLE_PRESETS.find(
        (p) => p.name !== preset.name
            && p.roles.every((r) => selectedRoles.includes(r))
            && preset.roles.every((r) => p.roles.includes(r))
    );
    return covering ? covering.name : null;
};

export const getLoanStatusColor = (loanStatus) => {
    switch(loanStatus) {
        case 'closed':
            return cyan[600];
        case 'voided':
            return red[400];
        case 'returned':
            return purple[300];
        case 'not funding':
            return orange[500];
        case 'active':
        default:
            return green[400];
    }
}

export const getNotificationCategory = (categoryCode) => {
    switch(categoryCode) {
        case 'internal_funding_daily_processing':
            return 'Internal Funding';
        case 'dealer_paid_daily_processing':
            return 'Merchant Payment';
        case 'dealer_return_daily_processing':
            return 'Merchant Return';
        case 'compliance_ach_update':
            return 'Compliance Update';
        default:
            return 'Uncategorized';
    }
}

export const commentCategories = [
    {value: "HomeOwner Contact"},
    {value: "Dealer Contact"},
    {value: "Loan Changes"},
    {value: "Funding Call"},
    {value: "Collections"},
    {value: "Hold"},
    {value: "Other"},
    {value: "Pop-up"},
];

export const dealerCommentCategories = [
    {value: "Flags"},
    {value: "Disputes"},
    {value: "AFCap"},
    {value: "Issues"},
    {value: "Other"},
    {value: "Pop-up"},
]

export const RETURN_STATUS = {
    ISSUED: "issued",
    PROCESSED: "processed",
    BOUNCED: "bounced",
}

export const LOAN_HOLD_STATUS = {
    HOLD_FOR_FUNDING: "Hold for funding",
    HOLD_FOR_PAYMENT: "Hold for payment",
    RELEASE_FOR_FUNDING: "Release for funding",
    RELEASE_FOR_PAYMENT: "Release for payment",
    PENDING_RELEASE: "Pending release tomorrow",
}